import {
  ChangeDetectorRef,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TenantConfigService } from 'src/app/shared/services/tenant-config.service';
import { NotificationService } from 'src/app/core/common-services/notification.service';
import { UserService } from 'src/app/core/common-services/user.services';
import { LanguageService } from 'src/app/shared/services/language.service';
import { SignalrService } from 'src/app/shared/services/signalr.service';
import { FaqDataService } from 'src/app/core/data-services/faq/faq.data.service';
import { JariValidators } from 'src/app/shared/shared/jari-validators';
import { hasError } from 'src/app/core/base/base';
import { InvitationRegistrationDataService } from 'src/app/core/data-services/identity/invitation-registration.data.service';
import {
  InvitationDetailsDto,
  InvitationLinkResponseDto,
  InvitationLinkStatusType,
} from 'src/app/core/data-models/invitation-registration.data.models';

@Component({
  selector: 'app-complete-register',
  templateUrl: './complete-register.component.html',
  styleUrls: ['./complete-register.component.scss'],
})
export class CompleteRegisterComponent implements OnInit, OnDestroy {
  @ViewChild('termsDialogRef') termsDialogRef!: TemplateRef<any>;

  companyName = '';
  token = '';

  // Link status state & API metadata
  invitationData: InvitationLinkResponseDto | null = null;
  isLoadingLink = true;
  linkStatus:
    | InvitationLinkStatusType
    | 'Valid'
    | 'Expired'
    | 'Cancelled'
    | 'Invalid'
    | 'Used'
    | null = null;
  linkMessage = '';
  logoUrl = '';
  primaryColor = '';
  steps: string[] = ['verify', 'confirm', 'password', 'terms', 'activate'];

  // Stepper state: 1 = Verify Identity (OTP), 2 = Confirm Details, 3 = Create Password & Activate
  currentStep = 1;

  // Step 1: OTP State
  otpLength = 6;
  otpDigits: string[] = ['', '', '', '', '', ''];
  isSendingOtp = false;
  isVerifyingOtp = false;
  isOtpVerified = false;
  maskedPhoneNumber = '';
  wrongAttempts = 0;
  readonly maxWrongAttempts = 5;

  // Timers
  expirationSeconds = 300; // default 5 minutes validity
  resendCooldownSeconds = 60; // default 60 seconds cooldown
  private defaultExpirationSeconds = 300;
  private defaultResendCooldownSeconds = 60;
  private timerIntervalId: any = null;
  private expirationIntervalId: any = null;
  private resendIntervalId: any = null;

  // Step 2: Details State
  isLoadingDetails = false;
  details: InvitationDetailsDto = {};
  showSupportInfo = false;

  // Step 3: Password & Terms State
  passwordForm!: FormGroup;
  hidePassword = true;
  hideConfirmPassword = true;
  acceptTerms = false;
  isActivating = false;
  hasError = hasError;

  passwordRules = {
    minChars: false,
    hasCapital: false,
    hasNumber: false,
    hasSpecial: false,
    matches: false,
  };

  // Terms Dialog State
  termsData: any = null;
  isLoadingTerms = false;
  private termsDialogRefInstance?: MatDialogRef<any>;

  constructor(
    public tenantConfigService: TenantConfigService,
    public languageService: LanguageService,
    private invitationService: InvitationRegistrationDataService,
    private notificationService: NotificationService,
    private userService: UserService,
    private signalrService: SignalrService,
    private faqDataService: FaqDataService,
    private matDialog: MatDialog,
    private route: ActivatedRoute,
    private router: Router,
    private ngZone: NgZone,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {
    this.companyName =
      localStorage.getItem('currentLang') === 'ar' &&
      this.tenantConfigService.tenant.companyNameAr
        ? this.tenantConfigService.tenant.companyNameAr
        : this.tenantConfigService.tenant.companyName || 'Miyar';
  }

  ngOnInit(): void {
    // Extract token from route parameters or query parameters
    this.token =
      this.route.snapshot.paramMap.get('token') ||
      this.route.snapshot.paramMap.get('otp') ||
      this.route.snapshot.queryParams['token'] ||
      '';

    console.log(this.token);

    this.initPasswordForm();

    if (!this.token) {
      this.isLoadingLink = false;
      this.linkStatus = 'Invalid';
      this.linkMessage = this.getFallbackMessage('Invalid');
      return;
    }

    this.validateInvitationLink();
  }

  ngOnDestroy(): void {
    this.clearAllTimers();
    if (this.termsDialogRefInstance) {
      this.termsDialogRefInstance.close();
    }
  }

  // ==========================================================
  // LINK VALIDATION
  // ==========================================================

  validateInvitationLink(): void {
    this.isLoadingLink = true;
    this.invitationService.checkLink(this.token).subscribe({
      next: (res) => {
        this.isLoadingLink = false;
        const linkData = this.extractLinkData(res);
        this.invitationData = linkData;

        // Apply tenant / branding data if provided in response
        this.applyLinkDataBranding(linkData);

        const rawStatus = (
          linkData.Status ||
          linkData.status ||
          (res as any)?.status ||
          ''
        )
          .toString()
          .trim();

        const statusKey = this.normalizeStatus(rawStatus);

        const redirectTo = linkData.RedirectTo || linkData.redirectTo;
        if (redirectTo) {
          if (
            redirectTo.startsWith('http://') ||
            redirectTo.startsWith('https://')
          ) {
            window.location.href = redirectTo;
          } else {
            this.router.navigateByUrl('/authentication/login');
          }
          this.notificationService.error(res.data?.message || '');

          return;
        }

        if (statusKey === 'Valid') {
          this.linkStatus = 'Valid';
          this.currentStep = 1;
          const phone =
            linkData.MaskedPhone ||
            linkData.maskedPhone ||
            linkData.MaskedPhoneNumber ||
            linkData.maskedPhoneNumber ||
            linkData.phoneNumber ||
            (linkData as any).PhoneNumber;
          if (phone) {
            this.maskedPhoneNumber = phone;
          }

          // Configure OTP length & timers from API response if provided
          this.configureOtpSettings(linkData);

          // Start timers immediately so the countdown is never frozen on page load
          this.startOtpTimers();

          // Automatically trigger sending the OTP code
          this.sendOtp();
        } else if (statusKey === 'Used') {
          this.linkStatus = 'Used';
          this.router.navigate(['/authentication/login']);
        } else if (statusKey === 'Expired') {
          this.linkStatus = 'Expired';
          const msg =
            linkData.Message ||
            linkData.message ||
            (res as any)?.message ||
            this.getFallbackMessage('Expired');
          this.linkMessage = this.formatSupportMessage(msg, 'Expired');
        } else if (statusKey === 'Cancelled') {
          this.linkStatus = 'Cancelled';
          const msg =
            linkData.Message ||
            linkData.message ||
            (res as any)?.message ||
            this.getFallbackMessage('Cancelled');
          this.linkMessage = this.formatSupportMessage(msg, 'Cancelled');
        } else {
          this.linkStatus = 'Invalid';
          const msg =
            linkData.Message ||
            linkData.message ||
            (res as any)?.message ||
            this.getFallbackMessage('Invalid');
          this.linkMessage = this.formatSupportMessage(msg, 'Invalid');
        }
      },
      error: (err) => {
        this.isLoadingLink = false;
        const errorData = this.extractLinkData(err?.error);
        if (errorData) {
          this.invitationData = errorData;
          this.applyLinkDataBranding(errorData);
        }

        const rawStatus = (
          errorData.Status ||
          errorData.status ||
          err?.error?.status ||
          ''
        )
          .toString()
          .trim();
        const statusKey = this.normalizeStatus(rawStatus);

        const redirectTo = errorData.RedirectTo || errorData.redirectTo;
        if (redirectTo) {
          if (
            redirectTo.startsWith('http://') ||
            redirectTo.startsWith('https://')
          ) {
            window.location.href = redirectTo;
          } else {
            this.router.navigateByUrl(redirectTo);
          }
          return;
        }

        if (statusKey === 'Used') {
          this.linkStatus = 'Used';
          this.router.navigate(['/authentication/login']);
          return;
        }

        if (statusKey === 'Expired') {
          this.linkStatus = 'Expired';
          const msg =
            errorData.Message ||
            errorData.message ||
            err?.error?.message ||
            this.getFallbackMessage('Expired');
          this.linkMessage = this.formatSupportMessage(msg, 'Expired');
          return;
        }

        if (statusKey === 'Cancelled') {
          this.linkStatus = 'Cancelled';
          const msg =
            errorData.Message ||
            errorData.message ||
            err?.error?.message ||
            this.getFallbackMessage('Cancelled');
          this.linkMessage = this.formatSupportMessage(msg, 'Cancelled');
          return;
        }

        this.linkStatus = 'Invalid';
        const msg =
          errorData.Message ||
          errorData.message ||
          err?.error?.message ||
          this.getFallbackMessage('Invalid');
        this.linkMessage = this.formatSupportMessage(msg, 'Invalid');
      },
    });
  }

  private extractLinkData(res: any): InvitationLinkResponseDto {
    if (!res) return {} as InvitationLinkResponseDto;
    return res.data || res.resource || res;
  }

  private applyLinkDataBranding(linkData: InvitationLinkResponseDto): void {
    if (!linkData) return;

    const clientName = linkData.ClientName || linkData.clientName;
    if (clientName) {
      this.companyName = clientName;
    }

    const logo = linkData.LogoUrl || linkData.logoUrl;
    if (logo) {
      this.logoUrl = logo;
      if (this.tenantConfigService?.tenant) {
        this.tenantConfigService.tenant.coloredLogoUrl = logo;
      }
    }

    const primaryCol = linkData.PrimaryColor || linkData.primaryColor;
    if (primaryCol) {
      this.primaryColor = primaryCol;
      document.documentElement.style.setProperty(
        '--invitation-primary-color',
        primaryCol,
      );
    }

    const steps = linkData.Steps || linkData.steps;
    if (steps && Array.isArray(steps) && steps.length > 0) {
      this.steps = steps;
    }

    const email = linkData.SupportEmail || linkData.supportEmail;
    if (email && this.tenantConfigService?.tenant) {
      this.tenantConfigService.tenant.supportEmail = email;
    }

    const phone = linkData.SupportPhone || linkData.supportPhone;
    if (phone && this.tenantConfigService?.tenant) {
      this.tenantConfigService.tenant.tollFreeNumber = phone;
    }

    const invLang = linkData.InvitationLanguage || linkData.invitationLanguage;
    if (invLang && (invLang === 'ar' || invLang === 'en')) {
      if (this.languageService.currentLanguage !== invLang) {
        this.languageService.setLanguage(invLang);
      }
    }
  }

  private configureOtpSettings(linkData: InvitationLinkResponseDto): void {
    const otpLen = linkData.OtpLength ?? linkData.otpLength ?? 6;
    if (otpLen && otpLen > 0) {
      this.otpLength = otpLen;
      if (this.otpDigits.length !== otpLen) {
        this.otpDigits = new Array(otpLen).fill('');
      }
    }

    const validMinutes = linkData.OtpValidMinutes ?? linkData.otpValidMinutes;
    if (
      validMinutes != null &&
      !isNaN(Number(validMinutes)) &&
      Number(validMinutes) > 0
    ) {
      this.defaultExpirationSeconds = Number(validMinutes) * 60;
      this.expirationSeconds = this.defaultExpirationSeconds;
    }

    const resendSeconds =
      linkData.OtpResendSeconds ?? linkData.otpResendSeconds;
    if (
      resendSeconds != null &&
      !isNaN(Number(resendSeconds)) &&
      Number(resendSeconds) >= 0
    ) {
      this.defaultResendCooldownSeconds = Number(resendSeconds);
      this.resendCooldownSeconds = this.defaultResendCooldownSeconds;
    }
  }

  private normalizeStatus(
    status: string,
  ): InvitationLinkStatusType | 'Invalid' {
    const s = status.toLowerCase();
    if (s === 'valid') return 'Valid';
    if (s === 'used') return 'Used';
    if (s === 'expired') return 'Expired';
    if (s === 'cancelled' || s === 'canceled') return 'Cancelled';
    return 'Invalid';
  }

  // ==========================================================
  // STEP 1: OTP VERIFICATION
  // ==========================================================

  sendOtp(): void {
    if (this.isSendingOtp) return;
    this.isSendingOtp = true;
    this.otpDigits = new Array(this.otpLength).fill('');
    this.wrongAttempts = 0;

    // Start timers immediately so the countdown ticks right away and is never frozen
    this.startOtpTimers();

    this.invitationService
      .sendOtp(this.token)
      .pipe(
        finalize(() => {
          this.isSendingOtp = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          const resData = (res as any)?.data || res;
          const phone =
            (res as any)?.MaskedPhone ||
            (res as any)?.data?.MaskedPhone ||
            res?.data?.maskedPhoneNumber ||
            (res as any)?.maskedPhone;
          if (phone) {
            this.maskedPhoneNumber = phone;
          } else if (!this.maskedPhoneNumber && res?.data?.phoneNumber) {
            this.maskedPhoneNumber = res.data.phoneNumber;
          }

          const expSec = resData?.ExpiresInSeconds ?? resData?.expiresInSeconds;
          if (expSec != null && !isNaN(Number(expSec)) && Number(expSec) > 0) {
            this.defaultExpirationSeconds = Number(expSec);
          }

          const resendSec =
            resData?.ResendAfterSeconds ?? resData?.resendAfterSeconds;
          if (
            resendSec != null &&
            !isNaN(Number(resendSec)) &&
            Number(resendSec) >= 0
          ) {
            this.defaultResendCooldownSeconds = Number(resendSec);
          }

          // Restart timers with updated server durations if provided
          this.startOtpTimers();
          this.focusOtpInput(0);
        },
        error: (err) => {
          // If sending OTP failed, allow immediate resend so the investor is never locked out:
          this.resendCooldownSeconds = 0;
          // Ensure expiration timer continues ticking down:
          if (!this.timerIntervalId && this.expirationSeconds > 0) {
            this.startOtpTimers({ resetCooldown: false, resetExpiration: false });
          }
          const msg = this.extractErrorMessage(
            err,
            'Shared.SOMETHING_WENT_WRONG',
          );
          this.safeNotifyError(msg);
        },
      });
  }

  private startOtpTimers(options?: {
    resetCooldown?: boolean;
    resetExpiration?: boolean;
  }): void {
    const shouldResetCooldown = options?.resetCooldown ?? true;
    const shouldResetExpiration = options?.resetExpiration ?? true;

    if (shouldResetExpiration) {
      this.expirationSeconds = this.defaultExpirationSeconds;
    }
    if (shouldResetCooldown) {
      this.resendCooldownSeconds = this.defaultResendCooldownSeconds;
    }

    this.clearAllTimers();

    this.timerIntervalId = setInterval(() => {
      this.ngZone.run(() => {
        let hasChanges = false;

        if (this.expirationSeconds > 0) {
          this.expirationSeconds--;
          hasChanges = true;
        }

        if (this.resendCooldownSeconds > 0) {
          this.resendCooldownSeconds--;
          hasChanges = true;
        }

        if (this.expirationSeconds <= 0 && this.resendCooldownSeconds <= 0) {
          this.clearAllTimers();
        }

        if (hasChanges) {
          this.cdr.markForCheck();
        }
      });
    }, 1000);
  }

  private clearAllTimers(): void {
    if (this.timerIntervalId) {
      clearInterval(this.timerIntervalId);
      this.timerIntervalId = null;
    }
    if (this.expirationIntervalId) {
      clearInterval(this.expirationIntervalId);
      this.expirationIntervalId = null;
    }
    if (this.resendIntervalId) {
      clearInterval(this.resendIntervalId);
      this.resendIntervalId = null;
    }
  }

  get isCodeExpired(): boolean {
    return this.expirationSeconds <= 0;
  }

  get canResendOtp(): boolean {
    return this.resendCooldownSeconds <= 0 && !this.isSendingOtp;
  }

  get isWrongAttemptsExceeded(): boolean {
    return this.wrongAttempts >= this.maxWrongAttempts;
  }

  get formattedExpirationTime(): string {
    const mins = Math.floor(this.expirationSeconds / 60);
    const secs = this.expirationSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  get currentOtpValue(): string {
    return this.otpDigits.join('');
  }

  get isOtpComplete(): boolean {
    return this.otpDigits.every(
      (d) => d !== '' && d !== null && d !== undefined,
    );
  }

  onOtpBeforeInput(event: any): void {
    const data = event?.data;
    if (!data) return;
    const normalized = data.replace(/[٠-٩]/g, (d: string) =>
      '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString(),
    );
    if (/\D/.test(normalized)) {
      event.preventDefault();
    }
  }

  onOtpInput(event: any, index: number): void {
    const target = event.target as HTMLInputElement;
    let inputVal: string = target?.value || '';

    // Convert Arabic-Indic numerals to standard Western digits
    inputVal = inputVal.replace(/[٠-٩]/g, (d) =>
      '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString(),
    );
    // Strip all non-digit characters
    inputVal = inputVal.replace(/\D/g, '');

    // Synchronize the DOM element value immediately
    if (!inputVal) {
      this.otpDigits[index] = '';
      if (target) {
        target.value = '';
      }
      return;
    }

    // Handle multiple digits (e.g. mobile auto-fill)
    if (inputVal.length > 1) {
      const chars = inputVal.split('').slice(0, this.otpLength);
      for (let i = 0; i < chars.length; i++) {
        if (index + i < this.otpLength) {
          this.otpDigits[index + i] = chars[i];
          const el = document.getElementById(
            `otp-input-${index + i}`,
          ) as HTMLInputElement;
          if (el) {
            el.value = chars[i];
          }
        }
      }
      const nextFocus = Math.min(index + chars.length, this.otpLength - 1);
      this.focusOtpInput(nextFocus);
      if (
        this.isOtpComplete &&
        !this.isCodeExpired &&
        !this.isWrongAttemptsExceeded
      ) {
        this.verifyOtp();
      }
      return;
    }

    this.otpDigits[index] = inputVal;
    if (target) {
      target.value = inputVal;
    }

    if (index < this.otpLength - 1) {
      this.focusOtpInput(index + 1);
    }

    if (
      this.isOtpComplete &&
      !this.isCodeExpired &&
      !this.isWrongAttemptsExceeded
    ) {
      this.verifyOtp();
    }
  }

  onOtpKeydown(event: KeyboardEvent, index: number): void {
    // 1. Allow modifier combinations (Ctrl+C, Ctrl+V, Ctrl+A, Command+C, etc.)
    if (event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    // 2. Allow special control/navigation keys
    const allowedNavigationKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Enter',
      'Escape',
    ];
    if (allowedNavigationKeys.includes(event.key)) {
      if (event.key === 'Backspace') {
        if (!this.otpDigits[index] && index > 0) {
          this.otpDigits[index - 1] = '';
          const prevEl = document.getElementById(
            `otp-input-${index - 1}`,
          ) as HTMLInputElement;
          if (prevEl) {
            prevEl.value = '';
          }
          this.focusOtpInput(index - 1);
        } else {
          this.otpDigits[index] = '';
          const target = event.target as HTMLInputElement;
          if (target) {
            target.value = '';
          }
        }
      } else if (event.key === 'Delete') {
        this.otpDigits[index] = '';
        const target = event.target as HTMLInputElement;
        if (target) {
          target.value = '';
        }
      } else if (event.key === 'ArrowLeft') {
        const targetIndex =
          this.languageService.currentLanguage === 'ar' ? index + 1 : index - 1;
        if (targetIndex >= 0 && targetIndex < this.otpLength) {
          this.focusOtpInput(targetIndex);
        }
      } else if (event.key === 'ArrowRight') {
        const targetIndex =
          this.languageService.currentLanguage === 'ar' ? index - 1 : index + 1;
        if (targetIndex >= 0 && targetIndex < this.otpLength) {
          this.focusOtpInput(targetIndex);
        }
      }
      return;
    }

    // 3. Allow virtual keyboard / IME composition keys without blocking
    if (event.key === 'Unidentified' || event.isComposing || (event as any).keyCode === 229) {
      return;
    }

    // 4. Check if the key is a digit (standard 0-9 or Arabic-Indic ٠-٩)
    let digit = event.key;
    if (/[٠-٩]/.test(digit)) {
      digit = '٠١٢٣٤٥٦٧٨٩'.indexOf(digit).toString();
    }

    if (/^[0-9]$/.test(digit)) {
      this.otpDigits[index] = digit;
      const target = event.target as HTMLInputElement;
      if (target) {
        target.value = digit;
      }
      if (index < this.otpLength - 1) {
        this.focusOtpInput(index + 1);
      }
      if (
        this.isOtpComplete &&
        !this.isCodeExpired &&
        !this.isWrongAttemptsExceeded
      ) {
        this.verifyOtp();
      }
      event.preventDefault();
      return;
    }

    // 5. Block ANY other key (letters, symbols, punctuation, space, etc.)
    event.preventDefault();
  }

  onOtpPaste(event: ClipboardEvent): void {
    event.preventDefault();
    const clipData = event.clipboardData?.getData('text') || '';
    const digitsOnly = clipData
      .replace(/[٠-٩]/g, (d) => '٠١٢٣٤٥٦٧٨٩'.indexOf(d).toString())
      .replace(/\D/g, '')
      .slice(0, this.otpLength);

    if (digitsOnly) {
      const chars = digitsOnly.split('');
      for (let i = 0; i < this.otpLength; i++) {
        this.otpDigits[i] = chars[i] || '';
        const el = document.getElementById(
          `otp-input-${i}`,
        ) as HTMLInputElement;
        if (el) {
          el.value = chars[i] || '';
        }
      }
      const nextFocus = Math.min(chars.length, this.otpLength - 1);
      this.focusOtpInput(nextFocus);
      if (
        this.isOtpComplete &&
        !this.isCodeExpired &&
        !this.isWrongAttemptsExceeded
      ) {
        this.verifyOtp();
      }
    }
  }

  private focusOtpInput(index: number): void {
    setTimeout(() => {
      const el = document.getElementById(
        `otp-input-${index}`,
      ) as HTMLInputElement;
      if (el) {
        el.focus();
        el.select();
      }
    }, 50);
  }

  verifyOtp(): void {
    if (
      !this.isOtpComplete ||
      this.isVerifyingOtp ||
      this.isCodeExpired ||
      this.isWrongAttemptsExceeded
    ) {
      return;
    }

    this.isVerifyingOtp = true;
    const otp = this.currentOtpValue;

    this.invitationService
      .verifyOtp(this.token, otp)
      .pipe(
        finalize(() => {
          this.isVerifyingOtp = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res: any) => {
          const isFailed =
            res?.data?.errorCode != null ||
            res?.errorCode != null ||
            res?.success === false ||
            res?.Success === false ||
            res?.isSuccess === false ||
            res?.IsSuccess === false ||
            res?.isValid === false ||
            res?.IsValid === false ||
            res?.data === false ||
            res?.Status === 'Invalid' ||
            res?.Status === 'Failed' ||
            res?.Status === 'Error' ||
            res?.status === 'Invalid' ||
            res?.status === 'Failed' ||
            res?.status === 'Error';

          if (isFailed) {
            const msg = this.extractErrorMessage(res);
            this.handleFailedOtpAttempt(msg);
            return;
          }

          // True verification success
          this.isOtpVerified = true;
          this.clearAllTimers();
          this.notificationService.success('Invitation.OTP_VERIFIED_SUCCESS');
          this.loadDetailsAndProceed();
        },
        error: (err) => {
          const msg = this.extractErrorMessage(err);
          this.handleFailedOtpAttempt(msg);
        },
      });
  }

  private handleFailedOtpAttempt(apiMessage?: string): void {
    this.wrongAttempts++;
    if (this.wrongAttempts >= this.maxWrongAttempts) {
      this.safeNotifyError('Invitation.MAX_ATTEMPTS_EXCEEDED');
    } else {
      const remaining = this.maxWrongAttempts - this.wrongAttempts;
      const defaultMsg =
        this.languageService.currentLanguage === 'ar'
          ? `رمز التحقق غير صحيح. متبقي لديك ${remaining} محاولة.`
          : `Invalid verification code. You have ${remaining} attempt(s) remaining.`;
      const msgToShow =
        typeof apiMessage === 'string' && apiMessage.trim()
          ? apiMessage
          : defaultMsg;
      this.safeNotifyError(msgToShow);

      // Select first box so user can immediately type new digits
      this.focusOtpInput(0);
    }
  }

  private extractErrorMessage(
    err: any,
    fallbackKey: string = 'Shared.SOMETHING_WENT_WRONG',
  ): string {
    if (!err) return this.translateSafe(fallbackKey);
    if (typeof err === 'string' && err.trim()) return err.trim();
    if (typeof err.error === 'string' && err.error.trim())
      return err.error.trim();

    const errObj = err.error || err;

    const candidates = [
      errObj?.Message,
      errObj?.message,
      errObj?.data?.Message,
      errObj?.data?.message,
      errObj?.data?.errorMessage,
      errObj?.errorMessage,
      errObj?.title,
      errObj?.error_description,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate.trim();
      }
    }

    if (errObj?.errors && typeof errObj.errors === 'object') {
      const errorKeys = Object.keys(errObj.errors);
      if (errorKeys.length > 0) {
        const firstVal = errObj.errors[errorKeys[0]];
        if (
          Array.isArray(firstVal) &&
          firstVal.length > 0 &&
          typeof firstVal[0] === 'string'
        ) {
          return firstVal[0];
        }
        if (typeof firstVal === 'string') {
          return firstVal;
        }
      }
    }

    return this.translateSafe(fallbackKey);
  }

  private translateSafe(key: string): string {
    try {
      return this.translateService?.instant(key) || key;
    } catch {
      return key;
    }
  }

  private safeNotifyError(
    rawMsg: any,
    fallbackKey: string = 'Shared.SOMETHING_WENT_WRONG',
  ): void {
    try {
      const msgStr =
        typeof rawMsg === 'string' && rawMsg.trim()
          ? rawMsg.trim()
          : this.extractErrorMessage(rawMsg, fallbackKey);
      this.notificationService.error(msgStr);
    } catch (e) {
      console.error('Failed to display notification error:', e);
    }
  }

  // ==========================================================
  // STEP 2: CONFIRM DETAILS
  // ==========================================================

  private loadDetailsAndProceed(): void {
    this.isLoadingDetails = true;
    this.invitationService.getDetails(this.token).subscribe({
      next: (res) => {
        this.isLoadingDetails = false;
        this.details = res?.data || res?.resource || (res as any) || {};
        this.currentStep = 2;
      },
      error: (err) => {
        this.isLoadingDetails = false;
        // Even if details call fails, allow proceeding to details step so the user isn't permanently blocked
        this.details = {};
        this.currentStep = 2;
        const msg = err?.error?.data?.message || err?.error?.message;
        if (msg) {
          this.notificationService.error(msg);
        }
      },
    });
  }

  get displayedNationalId(): string {
    return (
      this.details.NationalId ||
      this.details.NationalID ||
      this.details.IdentityNumber ||
      this.details.nationalId ||
      this.details.nationalID ||
      this.details.identityNumber ||
      '—'
    );
  }

  get displayedEmail(): string {
    return (
      this.details.Email ||
      this.details.EmailAddress ||
      this.details.email ||
      this.details.emailAddress ||
      '—'
    );
  }

  get displayedBirthDate(): string {
    const raw =
      this.details.BirthDate ||
      this.details.DateOfBirth ||
      this.details.birthDate ||
      this.details.dateOfBirth;
    if (!raw) return '—';
    try {
      const date = new Date(raw);
      if (isNaN(date.getTime())) return raw.toString();
      const yyyy = date.getFullYear();
      const mm = String(date.getMonth() + 1).padStart(2, '0');
      const dd = String(date.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    } catch {
      return raw.toString();
    }
  }

  get displayedPhone(): string {
    return (
      this.details.PhoneNumber ||
      this.details.MobileNumber ||
      this.details.Phone ||
      this.details.MaskedPhone ||
      this.details.phoneNumber ||
      this.details.mobileNumber ||
      this.details.phone ||
      this.maskedPhoneNumber ||
      '—'
    );
  }

  proceedToPasswordStep(): void {
    this.currentStep = 3;
  }

  // ==========================================================
  // STEP 3: CREATE PASSWORD & ACTIVATE
  // ==========================================================

  private initPasswordForm(): void {
    this.passwordForm = new FormGroup(
      {
        password: new FormControl('', [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(JariValidators.validPasswordRegex),
        ]),
        confirmPassword: new FormControl('', [
          Validators.required,
          Validators.minLength(8),
        ]),
      },
      { validators: this.passwordMatchValidator.bind(this) },
    );

    this.passwordForm.get('password')?.valueChanges.subscribe((val: string) => {
      this.evaluatePasswordRules(val || '');
    });

    this.passwordForm.get('confirmPassword')?.valueChanges.subscribe(() => {
      const pass = this.passwordForm.get('password')?.value || '';
      const confirm = this.passwordForm.get('confirmPassword')?.value || '';
      this.passwordRules.matches = !!pass && pass === confirm;
    });
  }

  private evaluatePasswordRules(val: string): void {
    this.passwordRules.minChars = val.length >= 8;
    this.passwordRules.hasCapital = /[A-Z]/.test(val);
    this.passwordRules.hasNumber = /\d/.test(val);
    this.passwordRules.hasSpecial = /[@$!%*?&]/.test(val);
    const confirm = this.passwordForm.get('confirmPassword')?.value || '';
    this.passwordRules.matches = !!val && val === confirm;
  }

  private passwordMatchValidator(
    group: AbstractControl,
  ): ValidationErrors | null {
    const pass = group.get('password')?.value;
    const confirm = group.get('confirmPassword')?.value;
    if (!pass || !confirm) return null;
    return pass === confirm ? null : { passwordNotMatch: true };
  }

  activateAccount(): void {
    if (this.isActivating || !this.acceptTerms || this.passwordForm.invalid) {
      return;
    }

    this.isActivating = true;
    const password = this.passwordForm.get('password')?.value;

    this.invitationService
      .activate({
        token: this.token,
        password: password,
        acceptTerms: true,
      })
      .subscribe({
        next: (res) => {
          this.isActivating = false;
          const dataAny = res?.data as any;
          if (dataAny?.errorCode != null || res?.success === false) {
            const msg =
              dataAny?.message || res?.message || 'Shared.SOMETHING_WENT_WRONG';
            this.notificationService.error(msg);
            return;
          }

          this.notificationService.success('Invitation.ACTIVATION_SUCCESS');

          // Save sign-in data
          if (res?.data) {
            this.userService.currentUserCredantials = res.data;

            if (res.data.currentUsedInvestorAccountId) {
              this.signalrService.startConnection(
                res.data.currentUsedInvestorAccountId,
              );
            }

            const targetRoute =
              res.data.isCompleteProfile === true ||
              res.data.userKYCStatus === 'Approved' ||
              res.data.userKYCStatus === 'Pending'
                ? ['/opportunities/opportunity-list']
                : ['/kyc/kyc-questions'];

            this.router
              .navigate(targetRoute)
              .then((navigated) => {
                if (!navigated) {
                  this.router.navigate(['/opportunities/opportunity-list']);
                }
              })
              .catch(() => {
                this.router.navigate(['/opportunities/opportunity-list']);
              });
          } else {
            this.router.navigate(['/authentication/login']);
          }
        },
        error: (err) => {
          this.isActivating = false;
          const msg =
            err?.error?.data?.message ||
            err?.error?.message ||
            'Shared.SOMETHING_WENT_WRONG';
          this.notificationService.error(msg);
        },
      });
  }

  // ==========================================================
  // TERMS AND CONDITIONS MODAL
  // ==========================================================

  openTermsModal(): void {
    this.isLoadingTerms = true;
    this.termsDialogRefInstance = this.matDialog.open(this.termsDialogRef, {
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '85vh',
    });

    this.faqDataService.TermsAndConditions().subscribe({
      next: (res) => {
        this.isLoadingTerms = false;
        this.termsData = res?.data;
      },
      error: () => {
        this.isLoadingTerms = false;
      },
    });
  }

  agreeToTermsFromModal(): void {
    this.acceptTerms = true;
    if (this.termsDialogRefInstance) {
      this.termsDialogRefInstance.close();
    }
  }

  closeTermsModal(): void {
    if (this.termsDialogRefInstance) {
      this.termsDialogRefInstance.close();
    }
  }

  // ==========================================================
  // SUPPORT CONTACTS HELPERS
  // ==========================================================

  get supportEmail(): string {
    return (
      this.invitationData?.SupportEmail ||
      this.invitationData?.supportEmail ||
      this.tenantConfigService.tenant.supportEmail ||
      'info@miyarcapital.com'
    );
  }

  get supportPhone(): string {
    return (
      this.invitationData?.SupportPhone ||
      this.invitationData?.supportPhone ||
      this.tenantConfigService.tenant.tollFreeNumber ||
      '920 032 099'
    );
  }

  private formatSupportMessage(
    rawMessage: string,
    type: 'Expired' | 'Cancelled' | 'Invalid',
  ): string {
    let msg = rawMessage || this.getFallbackMessage(type);
    msg = msg.replace(/\{SUPPORT_EMAIL[\)\}]/gi, this.supportEmail);
    msg = msg.replace(/\{SUPPORT_PHONE[\)\}]/gi, this.supportPhone);
    return msg;
  }

  private getFallbackMessage(
    type: 'Expired' | 'Cancelled' | 'Invalid',
  ): string {
    const isAr = this.languageService.currentLanguage === 'ar';
    if (type === 'Expired') {
      return isAr
        ? `انتهت صلاحية هذه الدعوة. يرجى التواصل مع ${this.supportEmail} أو ${this.supportPhone}.`
        : `This invitation has expired. Please contact ${this.supportEmail} or ${this.supportPhone}.`;
    }
    if (type === 'Cancelled') {
      return isAr
        ? `هذه الدعوة لم تعد صالحة. يرجى التواصل مع ${this.supportEmail} أو ${this.supportPhone}.`
        : `This invitation is no longer valid. Please contact ${this.supportEmail} or ${this.supportPhone}.`;
    }
    return isAr
      ? `هذه الدعوة غير صالحة. يرجى التواصل مع ${this.supportEmail} أو ${this.supportPhone}.`
      : `This invitation is no longer valid. Please contact ${this.supportEmail} or ${this.supportPhone}.`;
  }
}
